'use client';

import React, { useState } from 'react';
import { Share2, Mail } from 'lucide-react';

const SocialShare = () => {
  const [showIcons, setShowIcons] = useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = typeof window !== 'undefined' ? document.title : '';

  const handleShare = (platform: string) => {
    if (platform === 'native') {
      if (navigator.share) {
        navigator.share({
          title: shareTitle,
          url: shareUrl
        });
        return;
      }
      // Fallback to email if native sharing not supported
      platform = 'email';
    }

    let url = '';
    switch(platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        break;
      case 'pinterest':
        url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareTitle)}`;
        break;
      case 'reddit':
        url = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}`;
        break;
    }
    
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="relative">
      {/* Share Button */}
      <button
    onClick={() => setShowIcons(!showIcons)}
    className="flex items-center p-2 hover:bg-gray-100 rounded-full transition-all"
  >
    <span className="text-sm text-gray-800">Share</span>
    <Share2 className="w-5 h-5 ml-2" />
  </button>

      {/* Social Icons */}
      <div className={`absolute right-0 top-12 flex items-center gap-2 transition-all duration-300 ${
        showIcons ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}>
        <button 
          onClick={() => handleShare('facebook')}
          aria-label="Share on Facebook"
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        </button>
        
        <button 
          onClick={() => handleShare('twitter')}
          aria-label="Share on Twitter" 
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#1DA1F2">
            <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
          </svg>
        </button>

        <button 
          onClick={() => handleShare('pinterest')}
          aria-label="Share on Pinterest"
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#E60023">
            <path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
          </svg>
        </button>

        <button 
          onClick={() => handleShare('reddit')}
          aria-label="Share on Reddit"
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#FF4500">
            <path d="M24 11.779c0-1.459-1.192-2.645-2.657-2.645-.715 0-1.363.285-1.837.748-1.814-1.191-4.271-1.949-6.97-2.036l1.483-4.669 4.016.941c.018.526.448.944.975.944.539 0 .975-.436.975-.975 0-.537-.436-.972-.975-.972-.275 0-.524.113-.705.295l-5.018-1.177c-.18-.043-.367.063-.412.243l-1.659 5.224c-2.838.036-5.388.832-7.245 2.071-.477-.471-1.136-.764-1.864-.764-1.465 0-2.657 1.186-2.657 2.645 0 1.096.666 2.037 1.613 2.439-.041.256-.062.517-.062.781 0 4.085 4.65 7.398 10.388 7.398 5.737 0 10.388-3.313 10.388-7.398 0-.264-.021-.525-.062-.781.947-.402 1.613-1.343 1.613-2.439zm-16.777 1.816c0-.718.582-1.299 1.299-1.299.718 0 1.299.582 1.299 1.299 0 .718-.582 1.299-1.299 1.299-.717 0-1.299-.581-1.299-1.299zm9.723 4.816c-.707.707-1.939.707-2.646 0-.339-.339-.529-.78-.529-1.237s.19-.898.529-1.237c.707-.707 1.939-.707 2.646 0 .339.339.529.78.529 1.237s-.19.898-.529 1.237zm.199-3.517c-.718 0-1.299-.582-1.299-1.299 0-.718.582-1.299 1.299-1.299.717 0 1.299.582 1.299 1.299 0 .717-.582 1.299-1.299 1.299z"/>
          </svg>
        </button>

        <button 
          onClick={() => handleShare('email')}
          aria-label="Share via Email"
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <Mail className="w-6 h-6 text-gray-600" />
        </button>

        <button 
          onClick={() => handleShare('native')}
          aria-label="Share using native share"
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-600">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7 0-.24-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92 0-1.61-1.31-2.92-2.92-2.92z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SocialShare;
